// PATCH: Refactored to match ComparisonCardHomepage.tsx look and structure
// File: ComparisonCardCompare.tsx

import React from "react";
import { Link } from "react-router-dom";
import PromoCTAButtonCompare from "@/components/PromoCTAButtonCompare";

interface Firm {
  id: string;
  slug?: string;
  name: string;
  logo?: string;
  promo?: {
    percent?: number;
    code?: string;
    validUntil?: string;
    validUntilRemark?: string;
    firstPurchaseOnly?: boolean;
  };
  affiliateLink?: string;
  isBestValue?: boolean;
}

interface Price {
  fee: number;
  accountSize: number;
  maxDrawdownPercent?: number;
  dailyDrawdownPercent?: number;
  leverage?: string;
  label?: string;
  profitSplit?: number;
}

interface Props {
  firm: Firm;
  price: Price;
}

const ComparisonCardCompare: React.FC<Props> = ({ firm, price }) => {
  const discount = firm.promo?.percent || 0;
  const discountedFee = discount ? price.fee * (1 - discount / 100) : price.fee;

  return (
    <div
      className={`bg-gradient-to-br from-[#161616] to-[#111] border rounded-2xl p-4 text-yellow-100 space-y-3 shadow-md hover:shadow-xl hover:-translate-y-1 transition-transform ${firm.isBestValue ? 'border-yellow-400/60' : 'border-gray-800'} hover:border-yellow-400`}
      aria-label={`Card for ${firm.name}`}
    >
      <div className="flex items-center gap-4 mb-4">
        {firm.logo && (
          <div className="bg-black p-2 rounded-lg">
            <img
              src={firm.logo}
              alt={`${firm.name} logo`}
              className="w-12 h-12 object-contain"
            />
          </div>
        )}
        <div className="text-yellow-300 font-semibold leading-tight">
          {firm.name}
          {price.label && <div className="text-xs italic text-yellow-500">{price.label}</div>}
        </div>
        {firm.isBestValue && (
          <span className="ml-auto text-xs text-yellow-300 bg-yellow-700/20 rounded px-2 py-0.5" aria-label="Best value badge">
            🔥 Best Deal
          </span>
        )}
      </div>

      <div className="divide-y divide-yellow-100/5 bg-[#181818]/80 rounded-xl p-2 shadow-inner-md md:p-4" style={{boxShadow: 'inset 0 2px 12px 0 rgba(255, 221, 100, 0.06)'}}>
        {/* Account Size */}
        <div className="flex items-center justify-between py-2 md:py-3">
          <div className="text-yellow-100/70 text-sm md:text-base">Account</div>
          <span className="bg-gradient-to-r from-yellow-800/50 to-yellow-600/30 border border-yellow-300/30 backdrop-blur px-3 py-1 rounded-full font-bold text-yellow-100 text-sm md:text-base shadow-sm min-w-[48px] flex items-center justify-center text-center select-all cursor-pointer">
            ${price.accountSize.toLocaleString()}
          </span>
        </div>
        {/* Max DD */}
        <div className="flex items-center justify-between py-2 md:py-3">
          <div className="text-yellow-100/70 text-sm md:text-base">Max DD</div>
          <span className="bg-gradient-to-r from-yellow-800/50 to-yellow-600/30 border border-yellow-300/30 backdrop-blur px-3 py-1 rounded-full font-bold text-yellow-100 text-sm md:text-base shadow-sm min-w-[48px] flex items-center justify-center text-center select-all cursor-pointer">
            {price.maxDrawdownPercent !== undefined ? `${price.maxDrawdownPercent}%` : '-'}
          </span>
        </div>
        {/* Daily DD */}
        <div className="flex items-center justify-between py-2 md:py-3">
          <div className="text-yellow-100/70 text-sm md:text-base">Daily DD</div>
          <span className="bg-gradient-to-r from-yellow-800/50 to-yellow-600/30 border border-yellow-300/30 backdrop-blur px-3 py-1 rounded-full font-bold text-yellow-100 text-sm md:text-base shadow-sm min-w-[48px] flex items-center justify-center text-center select-all cursor-pointer">
            {price.dailyDrawdownPercent !== undefined ? `${price.dailyDrawdownPercent}%` : '-'}
          </span>
        </div>
        {/* Leverage */}
        <div className="flex items-center justify-between py-2 md:py-3">
          <div className="text-yellow-100/70 text-sm md:text-base">Leverage</div>
          <span className="bg-gradient-to-r from-yellow-800/50 to-yellow-600/30 border border-yellow-300/30 backdrop-blur px-3 py-1 rounded-full font-bold text-yellow-100 text-sm md:text-base shadow-sm min-w-[48px] flex items-center justify-center text-center select-all cursor-pointer">
            {price.leverage || '-'}
          </span>
        </div>
        {/* Fee */}
        <div className="flex items-center justify-between py-2 md:py-3 border-0">
          <div className="text-yellow-100/70 text-sm md:text-base">Fee</div>
          {discount ? (
            <span className="flex items-center gap-1">
              <s className="text-gray-500">${price.fee.toFixed(2)}</s>{' '}
              <span className="bg-gradient-to-r from-yellow-800/50 to-yellow-600/30 border border-yellow-300/30 backdrop-blur px-3 py-1 rounded-full font-bold text-yellow-300 text-sm md:text-base shadow-sm min-w-[48px] flex items-center justify-center text-center select-all cursor-pointer">${discountedFee.toFixed(2)}</span>
            </span>
          ) : (
            <span className="bg-gradient-to-r from-yellow-800/50 to-yellow-600/30 border border-yellow-300/30 backdrop-blur px-3 py-1 rounded-full font-bold text-yellow-300 text-sm md:text-base shadow-sm min-w-[48px] flex items-center justify-center text-center select-all cursor-pointer">${price.fee.toFixed(2)}</span>
          )}
        </div>
      </div>

      <hr className="my-3 border-t border-gray-700/40" />

      <div className="flex flex-col md:flex-row gap-2 mt-2 justify-center items-center">
        <Link
          to={`/reviews/${firm.id.split('-')[0]}`}
          className="w-[140px] h-[44px] px-4 py-2 text-sm font-semibold text-yellow-400 border border-yellow-400 rounded transition text-center flex items-center justify-center hover:bg-yellow-900/10 active:scale-95"
          aria-label={`Read review for ${firm.name}`}
        >
          Read Review
        </Link>
        <PromoCTAButtonCompare
          fee={price.fee}
          firmId={firm.id}
          affiliateLink={firm.affiliateLink}
          code={firm.promo?.code}
          percent={firm.promo?.percent}
          validUntil={firm.promo?.validUntil}
          validUntilRemark={firm.promo?.validUntilRemark}
          firstPurchaseOnly={firm.promo?.firstPurchaseOnly}
          isBestValue={firm.isBestValue}
          className="w-[140px] h-[44px] px-4 py-2 text-sm font-semibold rounded flex items-center justify-center active:scale-95 transition-transform"
          aria-label={`Claim promo for ${firm.name}`}
        />
      </div>
    </div>
  );
};

export default ComparisonCardCompare;
